import { Link } from "react-router-dom";
import { Palette, Cookie } from "lucide-react";

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-hero-gradient">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-xl text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                ColorWonder
              </span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-slate-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
                <Cookie className="w-6 h-6 text-violet-600" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Cookie Policy
              </h1>
            </div>
            
            <p className="text-slate-500 mb-8">Last updated: January 26, 2025</p>

            <div className="prose prose-slate max-w-none">
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">1. What Are Cookies?</h2>
                <p className="text-slate-600 leading-relaxed">
                  Cookies are small text files stored on your device when you visit a website. They help websites 
                  remember your preferences and improve your browsing experience.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">2. How We Use Cookies</h2>
                <p className="text-slate-600 leading-relaxed mb-4">ColorWonder AI uses cookies for the following purposes:</p>
                
                <h3 className="text-lg font-semibold text-slate-700 mt-4 mb-2">Essential Cookies</h3>
                <p className="text-slate-600 leading-relaxed mb-4">
                  These cookies are necessary for the website to function properly. They enable core functionality 
                  such as user authentication and account security.
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                  <li><strong>session_token:</strong> Keeps you logged in during your session</li>
                  <li><strong>csrf_token:</strong> Protects against cross-site request forgery</li>
                </ul>

                <h3 className="text-lg font-semibold text-slate-700 mt-4 mb-2">Functional Cookies</h3>
                <p className="text-slate-600 leading-relaxed mb-4">
                  These cookies remember your preferences and choices to enhance your experience.
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                  <li><strong>theme_preference:</strong> Remembers your display preferences</li>
                  <li><strong>language:</strong> Stores your language preference</li>
                </ul>

                <h3 className="text-lg font-semibold text-slate-700 mt-4 mb-2">Analytics Cookies</h3>
                <p className="text-slate-600 leading-relaxed">
                  We may use analytics cookies to understand how visitors interact with our website. 
                  This helps us improve our services.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">3. Third-Party Cookies</h2>
                <p className="text-slate-600 leading-relaxed mb-4">We use the following third-party services that may set cookies:</p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li><strong>Stripe:</strong> For secure payment processing</li>
                  <li><strong>Google:</strong> For authentication (if using Google Sign-In)</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">4. Managing Cookies</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  You can control and manage cookies through your browser settings. Please note that disabling 
                  essential cookies may affect the functionality of our website.
                </p>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Here's how to manage cookies in popular browsers:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline">Google Chrome</a></li>
                  <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline">Mozilla Firefox</a></li>
                  <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline">Safari</a></li>
                  <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline">Microsoft Edge</a></li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">5. Cookie Retention</h2>
                <p className="text-slate-600 leading-relaxed">
                  Session cookies are deleted when you close your browser. Persistent cookies remain on your device 
                  for a set period or until you delete them. Our authentication cookies typically expire after 7 days.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">6. Updates to This Policy</h2>
                <p className="text-slate-600 leading-relaxed">
                  We may update this Cookie Policy from time to time. Any changes will be posted on this page 
                  with an updated revision date.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">7. Contact Us</h2>
                <p className="text-slate-600 leading-relaxed">
                  If you have questions about our use of cookies, please contact us at:{" "}
                  <a href="mailto:privacy@colorwonder.app" className="text-violet-600 hover:underline">
                    privacy@colorwonder.app
                  </a>
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;
